// ============================================================================
// Form styles — the one source for form controls (Design Constitution §4
// Forms, I01–I12, I05-B, Q01, Q35, Q36, Q41).
// ----------------------------------------------------------------------------
// Label: above the field, 13px / 600, ink (Title Case is the caller's copy).
// Field: white, 1px #D2D6DB, radius 12, padding 10×12, 15px; focus is a green
//   border plus a 2px ring (index.html input rules, which every native input
//   already gets — controlStyle repeats the resting values for inline callers).
// Help: gray 12.5px under the field; an error replaces it, in danger ink.
// Required: a red '*'; there are no '(optional)' marks.
// Segmented and Yes/No are the only solid selected states (#187A54, white
// text). Chips and choice tiles select with a green outline and green text.
// Every control is at least 44px tall (§3 tap targets).
// ============================================================================
import type {CSSProperties} from 'react';

export const fieldLabelStyle: CSSProperties = {
  display: 'block',
  fontSize: 13,
  fontWeight: 600,
  lineHeight: 1.3,
  color: 'var(--ink)',
  marginBottom: 6,
};

export const requiredMarkStyle: CSSProperties = {
  color: 'var(--danger-ink)',
  marginLeft: 2,
};

export const fieldHelpStyle: CSSProperties = {
  marginTop: 6,
  fontSize: 12.5,
  lineHeight: 1.4,
  color: 'var(--label)',
};

export const fieldErrorStyle: CSSProperties = {
  ...fieldHelpStyle,
  color: 'var(--danger-ink)',
  fontWeight: 600,
};

export const controlStyle: CSSProperties = {
  fontFamily: 'inherit',
  fontSize: 15,
  padding: '10px 12px',
  minHeight: 44,
  border: '1px solid var(--border-strong)',
  borderRadius: 12,
  width: '100%',
  outline: 'none',
  background: '#fff',
  color: 'var(--ink)',
  boxSizing: 'border-box',
};

// A control button that cannot act right now (F09): the same look at 45%
// opacity, no pointer. The shared :disabled guard keeps it from lifting.
export const disabledControlStyle: CSSProperties = {
  opacity: 0.45,
  cursor: 'not-allowed',
};

// A secondary container inside a form (a group of fields), radius 12 (§3).
export const formSectionStyle: CSSProperties = {
  background: 'var(--surface-2)',
  border: '1px solid var(--border)',
  borderRadius: 12,
  padding: 14,
  marginBottom: 10,
};

// Segmented control and Yes/No (I05-B): one bordered row of 44px segments.
// No overflow clipping, so a segment's §2 pop-out (lift and shadow) shows;
// the outer segments round their own corners instead. Segments may shrink
// (minWidth 0) so four fit a phone row (Q20 560px column, 390px screen).
export const segmentedGroupStyle: CSSProperties = {
  display: 'flex',
  border: '1px solid var(--border-strong)',
  borderRadius: 12,
  background: '#fff',
};

export function segmentStyle(selected: boolean, first: boolean, last = false): CSSProperties {
  return {
    flex: '1 1 0',
    minWidth: 0,
    minHeight: 44,
    padding: '10px 6px',
    border: 'none',
    borderLeft: first ? 'none' : '1px solid var(--border-strong)',
    borderRadius: `${first ? '11px' : '0'} ${last ? '11px 11px' : '0 0'} ${first ? '11px' : '0'}`,
    fontFamily: 'inherit',
    fontSize: 13,
    lineHeight: 1.2,
    fontWeight: selected ? 700 : 600,
    overflowWrap: 'anywhere',
    cursor: 'pointer',
    background: selected ? 'var(--brand)' : '#fff',
    color: selected ? '#fff' : 'var(--text-2)',
  };
}

// Checkbox chips (I07) and choice tiles (I08): off = gray border; on = green
// outline, green text and a check (Q01: outline, never solid).
export function chipStyle(selected: boolean): CSSProperties {
  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    minHeight: 44,
    padding: '10px 14px',
    borderRadius: 999,
    border: selected ? '1px solid var(--brand)' : '1px solid var(--border-strong)',
    background: '#fff',
    color: selected ? 'var(--brand)' : 'var(--ink)',
    fontFamily: 'inherit',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
  };
}

export function choiceTileStyle(selected: boolean): CSSProperties {
  return {
    position: 'relative',
    display: 'block',
    width: '100%',
    minHeight: 44,
    padding: selected ? '13px 15px' : '14px 16px',
    borderRadius: 12,
    border: selected ? '2px solid var(--brand)' : '1px solid var(--border-strong)',
    background: '#fff',
    color: 'var(--ink)',
    fontFamily: 'inherit',
    fontSize: 15,
    textAlign: 'left',
    cursor: 'pointer',
  };
}

// Locked / calculated value (I09): plain bold text, never an input-shaped box.
export const lockedValueStyle: CSSProperties = {
  fontSize: 15,
  fontWeight: 700,
  color: 'var(--ink)',
  lineHeight: 1.4,
};
